import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export function EmployeeLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div style={styles.container}>
      {/* Animated Background */}
      <div style={styles.backgroundOrbs}>
        <div style={styles.orb1} />
        <div style={styles.orb2} />
        <div style={styles.orb3} />
      </div>

      {/* Navigation */}
      <nav style={styles.nav}>
        <div style={styles.navInner}>
          <div style={styles.brandSection}>
            <div style={styles.logoContainer}>
              <img src="/logo.png" alt="BloomCube AI" style={styles.logoImage} />
              <span style={styles.logoText}>BloomCube AI</span>
            </div>
          </div>

          <div style={styles.navLinks}>
            <NavLink to="/employee" end style={({ isActive }) => ({
              ...styles.navLink,
              ...(isActive ? styles.navLinkActive : {}),
            })}>
              <span style={styles.navIcon}>📊</span>
              <span>Dashboard</span>
            </NavLink>
            <NavLink to="/employee/alerts" style={({ isActive }) => ({
              ...styles.navLink,
              ...(isActive ? styles.navLinkActive : {}),
            })}>
              <span style={styles.navIcon}>🔔</span>
              <span>Wellness Nudges</span>
            </NavLink>
            <NavLink to="/employee/sessions" style={({ isActive }) => ({
              ...styles.navLink,
              ...(isActive ? styles.navLinkActive : {}),
            })}>
              <span style={styles.navIcon}>📅</span>
              <span>Sessions</span>
            </NavLink>
          </div>

          <div style={styles.userSection}>
            <div 
              style={styles.userProfile}
              onClick={() => setIsProfileOpen(!isProfileOpen)}
            >
              <div style={styles.avatar}>
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div style={styles.userInfo}>
                <span style={styles.greeting}>{getGreeting()}</span>
                <span style={styles.userName}>{user?.name}</span>
              </div>
              <span style={styles.dropdownIcon}>▾</span>
            </div>

            {isProfileOpen && (
              <div style={styles.dropdown}>
                <div style={styles.dropdownHeader}>
                  <div style={styles.dropdownAvatar}>
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div>
                    <div style={styles.dropdownName}>{user?.name}</div>
                    <div style={styles.dropdownEmail}>{user?.email}</div>
                  </div>
                </div>
                <div style={styles.dropdownDivider} />
                <button onClick={handleLogout} style={styles.logoutBtn}>
                  <span>🚪</span>
                  <span>Sign Out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main style={styles.main}>
        <div style={styles.contentWrapper}>
          <Outlet />
        </div>
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <p style={styles.footerContent}><img src="/logo.png" alt="" style={styles.footerLogo} /> Take care of yourself — small breaks make a big difference</p>
      </footer>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #ECFDF5 0%, #F0FDFA 50%, #F0FDF4 100%)',
    position: 'relative',
    overflow: 'hidden',
  },
  backgroundOrbs: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
    zIndex: 0,
    overflow: 'hidden',
  },
  orb1: {
    position: 'absolute',
    width: '600px',
    height: '600px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(16, 185, 129, 0.15) 0%, transparent 70%)',
    top: '-200px',
    right: '-100px',
    animation: 'float 20s ease-in-out infinite',
  },
  orb2: {
    position: 'absolute',
    width: '400px',
    height: '400px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(6, 182, 212, 0.12) 0%, transparent 70%)',
    bottom: '-100px',
    left: '-50px',
    animation: 'float 15s ease-in-out infinite reverse',
  },
  orb3: {
    position: 'absolute',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(139, 92, 246, 0.08) 0%, transparent 70%)',
    top: '40%',
    left: '30%',
    animation: 'float 25s ease-in-out infinite',
  },
  nav: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    padding: '12px 24px',
  },
  navInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '12px 24px',
    background: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: '20px',
    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.6)',
  },
  brandSection: {
    display: 'flex',
    alignItems: 'center',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  logoImage: {
    width: '40px',
    height: '40px',
    objectFit: 'contain',
  },
  logoText: {
    fontSize: '22px',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  navLinks: {
    display: 'flex',
    gap: '8px',
  },
  navLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 18px',
    borderRadius: '12px',
    textDecoration: 'none',
    fontWeight: 500,
    fontSize: '14px',
    color: '#4B5563',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: '1px solid transparent',
  },
  navLinkActive: {
    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    color: 'white',
    boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)',
    transform: 'translateY(-1px)',
  },
  navIcon: {
    fontSize: '16px',
  },
  userSection: {
    position: 'relative',
  },
  userProfile: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 12px',
    borderRadius: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: '1px solid transparent',
  },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #10B981 0%, #06B6D4 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: '16px',
    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  greeting: {
    fontSize: '11px',
    color: '#6B7280',
    fontWeight: 500,
  },
  userName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F2937',
  },
  dropdownIcon: {
    fontSize: '12px',
    color: '#9CA3AF',
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    width: '280px',
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    overflow: 'hidden',
    animation: 'fadeIn 0.2s ease-out',
  },
  dropdownHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    background: 'linear-gradient(135deg, #ECFDF5 0%, #F0FDFA 100%)',
  },
  dropdownAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '14px',
    background: 'linear-gradient(135deg, #10B981 0%, #06B6D4 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: '18px',
  },
  dropdownName: {
    fontWeight: 600,
    color: '#1F2937',
    fontSize: '15px',
  },
  dropdownEmail: {
    fontSize: '13px',
    color: '#6B7280',
  },
  dropdownDivider: {
    height: '1px',
    background: '#E5E7EB',
  },
  logoutBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    padding: '14px 16px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    color: '#EF4444',
    transition: 'background 0.2s',
  },
  main: {
    position: 'relative',
    zIndex: 1,
    minHeight: 'calc(100vh - 160px)',
    padding: '24px',
  },
  contentWrapper: {
    maxWidth: '1400px',
    margin: '0 auto',
    animation: 'fadeIn 0.5s ease-out',
  },
  footer: {
    textAlign: 'center',
    padding: '24px',
    color: '#6B7280',
    fontSize: '14px',
    position: 'relative',
    zIndex: 1,
  },
  footerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    margin: 0,
  },
  footerLogo: {
    width: '16px',
    height: '16px',
    objectFit: 'contain',
  },
};
